import { useEffect, useRef } from "react";
import { noise3 } from "../noise";
import { useReducedMotion } from "../reduced-motion";
import { useCanvas, type Frame } from "./useCanvas";

interface Props {
  className?: string;
  /** Where the pit sits, as a fraction of the canvas (x, y). */
  focus?: [number, number];
  /** Multiplies line opacity; lower it behind dense text. */
  strength?: number;
}

const LEVEL_STEP = 0.052;
/** The contour drawn in the accent colour: a ring low in the pit. */
const ORE_LEVEL = 8;
const BONE = "228, 232, 227";
const LIME = "203, 255, 84";

/**
 * A slowly shifting topographic map: contour lines of a noise field with a
 * depression in it, so the lines close into the benches of a pit. Drawn with
 * marching squares on a coarse grid.
 */
export function ContourField({ className, focus = [0.7, 0.56], strength = 1 }: Props) {
  const reduced = useReducedMotion();
  const pointer = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const field = useRef<Float32Array>(new Float32Array(0));

  useEffect(() => {
    if (reduced) return;
    const onMove = (event: PointerEvent) => {
      pointer.current.tx = event.clientX / window.innerWidth - 0.5;
      pointer.current.ty = event.clientY / window.innerHeight - 0.5;
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, [reduced]);

  const draw = ({ ctx, width, height, time }: Frame) => {
    const p = pointer.current;
    p.x += (p.tx - p.x) * 0.04;
    p.y += (p.ty - p.y) * 0.04;

    const cell = Math.min(20, Math.max(11, width / 96));
    const cols = Math.ceil(width / cell) + 1;
    const rows = Math.ceil(height / cell) + 1;
    if (field.current.length !== cols * rows) field.current = new Float32Array(cols * rows);
    const values = field.current;

    const scale = Math.min(width, height) * 0.92;
    const pitX = (focus[0] + p.x * 0.05) * width;
    const pitY = (focus[1] + p.y * 0.05) * height;
    const drift = time * 0.045;

    for (let j = 0; j < rows; j++) {
      for (let i = 0; i < cols; i++) {
        const x = i * cell;
        const y = j * cell;
        const dx = (x - pitX) / scale;
        const dy = ((y - pitY) / scale) * 1.3;
        const bowl = 1 - Math.exp(-(dx * dx + dy * dy) / 0.15);
        const broad = noise3(x * 0.0021, y * 0.0021, drift);
        const fine = noise3(x * 0.0062 + 40, y * 0.0062, drift * 1.6);
        values[j * cols + i] = bowl * 0.86 + broad * 0.56 + fine * 0.11;
      }
    }

    ctx.clearRect(0, 0, width, height);
    ctx.lineWidth = 1;
    ctx.lineJoin = "round";

    const minor: number[] = [];
    const major: number[] = [];
    const ore: number[] = [];

    for (let j = 0; j < rows - 1; j++) {
      for (let i = 0; i < cols - 1; i++) {
        const v0 = values[j * cols + i];
        const v1 = values[j * cols + i + 1];
        const v2 = values[(j + 1) * cols + i + 1];
        const v3 = values[(j + 1) * cols + i];
        const low = Math.min(v0, v1, v2, v3);
        const high = Math.max(v0, v1, v2, v3);
        const first = Math.ceil(low / LEVEL_STEP);
        const last = Math.floor(high / LEVEL_STEP);
        if (first > last) continue;

        const x = i * cell;
        const y = j * cell;

        for (let k = first; k <= last; k++) {
          const level = k * LEVEL_STEP;
          const index = (v0 > level ? 1 : 0) | (v1 > level ? 2 : 0) | (v2 > level ? 4 : 0) | (v3 > level ? 8 : 0);
          if (index === 0 || index === 15) continue;

          // Where the contour crosses each edge of the cell.
          const topX = x + ((level - v0) / (v1 - v0)) * cell;
          const bottomX = x + ((level - v3) / (v2 - v3)) * cell;
          const leftY = y + ((level - v0) / (v3 - v0)) * cell;
          const rightY = y + ((level - v1) / (v2 - v1)) * cell;

          const out = k === ORE_LEVEL ? ore : k % 5 === 0 ? major : minor;
          const seg = (ax: number, ay: number, bx: number, by: number) => out.push(ax, ay, bx, by);

          switch (index) {
            case 1:
            case 14:
              seg(x, leftY, topX, y);
              break;
            case 2:
            case 13:
              seg(topX, y, x + cell, rightY);
              break;
            case 3:
            case 12:
              seg(x, leftY, x + cell, rightY);
              break;
            case 4:
            case 11:
              seg(x + cell, rightY, bottomX, y + cell);
              break;
            case 6:
            case 9:
              seg(topX, y, bottomX, y + cell);
              break;
            case 7:
            case 8:
              seg(x, leftY, bottomX, y + cell);
              break;
            case 5:
              seg(x, leftY, topX, y);
              seg(x + cell, rightY, bottomX, y + cell);
              break;
            case 10:
              seg(topX, y, x + cell, rightY);
              seg(x, leftY, bottomX, y + cell);
              break;
          }
        }
      }
    }

    const stroke = (segments: number[], style: string) => {
      ctx.strokeStyle = style;
      ctx.beginPath();
      for (let s = 0; s < segments.length; s += 4) {
        ctx.moveTo(segments[s], segments[s + 1]);
        ctx.lineTo(segments[s + 2], segments[s + 3]);
      }
      ctx.stroke();
    };

    stroke(minor, `rgba(${BONE}, ${0.13 * strength})`);
    stroke(major, `rgba(${BONE}, ${0.34 * strength})`);
    stroke(ore, `rgba(${LIME}, ${0.6 * strength})`);
  };

  const ref = useCanvas(draw, { animate: !reduced, maxDpr: 1.5 });

  return <canvas ref={ref} aria-hidden="true" className={className} />;
}
